/**
 * First run: which agent answers, and when the flow runs (RFC 0337 S6).
 *
 * It reads what the desktop shell found on this Mac (Claude Code,
 * Codex, Cursor), shows each one's state and the command that makes it ready,
 * and lets the person tick the ones they use: the composer offers only those,
 * and the first ticked is its default. "Invite people" sends real invites to
 * the project behind the flow (RFC 0345 S14), and is drawn only where sharing
 * is on and this device's account is open. A second step lists Featured from
 * the package registry and installs the ticked ones into the World (RFC 0279
 * S6); the sharing build adds the invitation step after it.
 *
 * The trigger is A NEW WORLD, not a new browser: the World is a deployment's
 * directory of projects (RFC 0261), so "the World was just created" is exactly
 * "this person has never used Mod here". `getOrCreateWorld` already reports it.
 *
 * Everything here is pure or localStorage-only, so the flow's rules are testable
 * without React.
 */

export interface OnboardingChoices {
  /** Harness ids from HARNESS_OPTIONS the person uses. The composer offers
   *  only these, and the first, in HARNESS_OPTIONS order, is its default. */
  harnesses?: string[]
  /** Lineage ids of the Featured packages the person ticked (RFC 0279 S6). */
  apps?: string[]
  /** When the flow was completed or skipped. */
  finishedAt?: number
}

/** The harnesses Mod can drive, in the order a new user most likely has them. */
export const HARNESS_OPTIONS = [
  {
    id: 'claude-code',
    name: 'Claude Code',
    detail: 'Anthropic. Runs on your Claude plan.',
  },
  { id: 'codex', name: 'Codex', detail: 'OpenAI. Runs on your ChatGPT plan.' },
  { id: 'cursor', name: 'Cursor', detail: 'Cursor’s agent, on your plan.' },
  {
    id: 'pi',
    name: 'Pi',
    detail: 'A small coding agent, on the providers you signed in to.',
  },
] as const

/** What the agent step shows for one harness. */
export interface Readiness {
  ready: boolean
  /** One or two words: "Ready", "Not logged in", "Not installed". */
  label: string
  /** The command or step that makes it ready. */
  remedy?: string
}

/**
 * One harness's state, from the shell's probe. `row` undefined with `known`
 * true means the shell reported this Mac and the harness is absent from it;
 * `known` false means nobody reported (a browser, not the desktop app), and the
 * step says nothing rather than claim it is missing.
 */
export function readiness(
  row: { state: string; remedy?: string } | undefined,
  known: boolean,
): Readiness | null {
  if (!known) return null
  if (!row) return { ready: false, label: 'Not installed' }
  switch (row.state) {
    case 'connected':
      return { ready: true, label: 'Ready' }
    case 'unauthenticated':
    case 'expired':
      return { ready: false, label: 'Not logged in', remedy: row.remedy }
    case 'unsupported':
      return { ready: false, label: 'Needs an update', remedy: row.remedy }
    case 'notOnPath':
      return { ready: false, label: 'Not found', remedy: row.remedy }
    default:
      return { ready: false, label: 'Not installed', remedy: row.remedy }
  }
}

/** What starts ticked before the person touches anything: every ready agent. */
export function readyHarnesses(
  statuses: ReadonlyArray<{ id: string; state: string }> | null,
): string[] {
  if (!statuses) return []
  return HARNESS_OPTIONS.map((h) => h.id).filter(
    (id) => statuses.find((s) => s.id === id)?.state === 'connected',
  )
}

/** The default among the ticked: the first in HARNESS_OPTIONS order. */
export function defaultHarness(ticked: readonly string[]): string | null {
  return HARNESS_OPTIONS.find((h) => ticked.includes(h.id))?.id ?? null
}

/**
 * The composer id for a harness: `harness:model`, the shape the composer's menu
 * writes, with the harness's first model. Null when it reports none, so the
 * composer keeps its own default rather than a harness with no model.
 */
export function composerChoiceFor(
  harnessId: string,
  local: ReadonlyArray<{ id: string; models?: string[] }>,
): string | null {
  const model = local.find((h) => h.id === harnessId)?.models?.[0]
  return model ? `${harnessId}:${model}` : null
}

const PENDING_KEY = 'mod_w_onboarding_pending'
const CHOICES_KEY = 'mod_w_onboarding_v1'

/**
 * Record that a World was just created, so the next project render offers the
 * flow. A flag rather than an immediate render because the World is created during
 * a redirect, and a flow that mounted there would be unmounted a moment later.
 */
export function noteWorldCreated(): void {
  try {
    // Never re-arm for someone who has already been through it: a stale pointer
    // can make `getOrCreateWorld` mint a second World for an existing user, and
    // re-onboarding them would be a bug, not a welcome.
    if (readChoices().finishedAt) return
    localStorage.setItem(PENDING_KEY, '1')
  } catch {
    // Onboarding is a nicety; losing the flag just means it does not run.
  }
}

export function isOnboardingPending(): boolean {
  try {
    return localStorage.getItem(PENDING_KEY) === '1'
  } catch {
    return false
  }
}

export function readChoices(): OnboardingChoices {
  try {
    const raw = localStorage.getItem(CHOICES_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object'
      ? (parsed as OnboardingChoices)
      : {}
  } catch {
    return {}
  }
}

/** Persist what has been chosen so far, merged over what was there. */
export function saveChoices(choices: OnboardingChoices): void {
  try {
    localStorage.setItem(
      CHOICES_KEY,
      JSON.stringify({ ...readChoices(), ...choices }),
    )
  } catch {
    // best-effort
  }
}

/** Finish (or skip): clear the flag and stamp it, so it never reappears. */
export function finishOnboarding(choices: OnboardingChoices = {}): void {
  saveChoices({ ...choices, finishedAt: Date.now() })
  try {
    localStorage.removeItem(PENDING_KEY)
  } catch {
    // best-effort
  }
}

/**
 * Re-arm the flow, for the `?onboarding=1` escape hatch — how the flow gets
 * reviewed or re-run after it has been completed once. Deliberately does not clear
 * previous choices: seeing them selected is the point.
 */
export function restartOnboarding(): void {
  try {
    localStorage.setItem(PENDING_KEY, '1')
  } catch {
    // best-effort
  }
}
